import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link, useNavigate, Route } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";

import UserPanel from "../components/UserPanel";
import {
  getMyQrsAction,
  ownerUpdateQR,
  ownerToggleLost,
  toggleHiddenCollar,
} from "../actions/orderActions";
import {
  ImagePlus,
  User,
  Phone,
  Building2,
  MapPin,
  Dog,
  PawPrint,
  VenusAndMars,
  Palette,
  MessageSquareMore,
  Syringe,
  Trash2,
} from "lucide-react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faLocationDot,
  faPencil,
  faLocationArrow,
} from "@fortawesome/free-solid-svg-icons";

import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// მარკერის აიქონის გასასწორებლად (Leaflet-ს ხშირად აქვს პრობლემა default icon-ზე React-ში)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ChangeView = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    // თუ ცენტრი არ არსებობს ან მისი რომელიმე კოორდინატი null-ია, არაფერი გააკეთო
    if (!center || center[0] === null || center[1] === null) return;

    map.flyTo(center, 16, {
      animate: true,
      duration: 1.5,
    });

    setTimeout(() => {
      map.invalidateSize();
    }, 200);
  }, [center, map]);
  return null;
};

const GenerateQR = ({
  getMyQrsAction,
  ownerUpdateQR,
  ownerToggleLost,
  toggleHiddenCollar,
  myQRS,
}) => {
  const { t } = useTranslation();

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const navigate = useNavigate();
  const [seenLocations, setSeenLocations] = useState(() => {
    const saved = {};
    // აქ შეგიძლია გადაუარო შენს QR-ებს და ამოიკითხო, მაგრამ
    // რეალურად getPinColor ამას ისედაც აკეთებს, ასე რომ ცარიელი {} -იც იმუშავებს.
    return {};
  });

  const orangeIcon = new L.DivIcon({
    className: "custom-orange-pin",
    html: renderToStaticMarkup(
      <FontAwesomeIcon icon={faLocationDot} className="custom-orange-pin" />,
    ),
    iconSize: [50, 50],
    iconAnchor: [25, 50], // პინის წვერი ზუსტად კოორდინატზე რომ იჯდეს
    popupAnchor: [0, -50], // პოპაპი პინის თავზე რომ გამოჩნდეს
  });

  const createAnimalIcon = (imageUrl) => {
    return new L.DivIcon({
      className: "custom-pet-icon",
      html: renderToStaticMarkup(
        <div className="pet-marker-container">
          <img
            src={imageUrl || "/img/pet-placeholder.webp"}
            alt="pet"
            className="pet-marker-image"
          />
          <div className="pet-marker-pin"></div>
        </div>,
      ),
      iconSize: [70, 70],
      iconAnchor: [35, 70],
      popupAnchor: [0, -70],
    });
  };

  useEffect(() => {
    getMyQrsAction();
  }, []);

  const getPinColor = (qr) => {
    // 1. თუ ლოკაცია საერთოდ არ აქვს
    if (!qr.last_location_lat || !qr.last_location_lng) return "#808080";

    // 2. ვნახულობთ დროს LocalStorage-ში ან State-ში
    const savedTime = localStorage.getItem(`seen_loc_${qr.uuid}`);
    const currentStateTime = seenLocations[qr.uuid];

    // თუ რომელიმეში წერია მიმდინარე ლოკაციის დრო, ე.ი. ნანახია
    if (
      savedTime === qr.last_location_time ||
      currentStateTime === qr.last_location_time
    ) {
      return "#808080"; // ნაცრისფერი (ნანახია)
    }

    return "#ff6500"; // ნარინჯისფერი (ახალია)
  };

  const getPinAnimation = (qr) => {
    // 1. თუ ლოკაცია საერთოდ არ აქვს - ანიმაციის გარეშე
    if (!qr.last_location_lat || !qr.last_location_lng) return "none";

    // 2. ვნახულობთ დროს LocalStorage-ში ან State-ში
    const savedTime = localStorage.getItem(`seen_loc_${qr.uuid}`);
    const currentStateTime = seenLocations[qr.uuid];

    // თუ რომელიმეში წერია მიმდინარე ლოკაციის დრო, ე.ი. ნანახია - ანიმაციის გარეშე
    if (
      savedTime === qr.last_location_time ||
      currentStateTime === qr.last_location_time
    ) {
      return "none";
    }

    // თუ ახალია, ვაძლევთ pulse ანიმაციას
    return "pulse-orange 1.5s infinite ease-in-out";
  };

  const handleShowOnMap = (e, qr) => {
    e.stopPropagation(); // რომ მოდალი არ გაიხსნას ღილაკზე დაჭერისას

    if (qr.last_location_time) {
      // ვინახავთ LocalStorage-ში (სამომავლოდ)
      localStorage.setItem(`seen_loc_${qr.uuid}`, qr.last_location_time);

      // აგზავნის სიგნალს, რომელსაც მენიუ დაიჭერს
      window.dispatchEvent(new Event("location-seen"));

      // ვაახლებთ State-ს (მომენტალური რეაგირებისთვის)
      setSeenLocations((prev) => ({
        ...prev,
        [qr.uuid]: qr.last_location_time,
      }));
    }

    setMapLocation({
      lat: qr.last_location_lat || null, // თუ არ არის, იქნება null
      lng: qr.last_location_lng || null,
      name: qr.pet_name || t("unnamed_pet"),
      time: qr.last_location_time || null,
      hasLocation: !!(qr.last_location_lat && qr.last_location_lng), // ბულეანი მოსახერხებელი შემოწმებისთვის
    });

    // რუკისკენ სქროლი (სურვილისამებრ)
    document
      .getElementById("pet-map-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleQRClick = (qr) => {
    setSelectedQR(qr);
    setEditFormData({
      qr_number: qr.qr_number,
      pet_name: qr.pet_name || "",
      pet_type: qr.pet_type || "dog",
      pet_breed: qr.pet_breed || "",
      pet_color: qr.pet_color || "",
      gender: qr.gender || "male",
      is_vaccinated: qr.is_vaccinated || false,
      full_name: qr.full_name || "",
      phone_number: qr.phone_number || "",
      second_phone_number: qr.second_phone_number || "",
      city: qr.city || "",
      address: qr.address || "",
      comment: qr.comment || "",
    });
    setEditImagePreview(qr.pet_image || null);
    setIsModalOpen(true);
  };

  const handleToggleHiddenClick = (qr) => {
    const identifier = qr.uuid || qr.qr_number || qr.id;
    if (!identifier) return;
    const confirmKey = qr.hidden_by_owner
      ? "show_collar_confirm"
      : "hide_collar_confirm";
    const confirmed = window.confirm(t(confirmKey));
    if (!confirmed) return;
    toggleHiddenCollar(identifier);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQR(null);
    setEditFormData({});
    setEditImagePreview(null);
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditFormData((prev) => ({ ...prev, pet_image: [file] }));
      const previewUrl = URL.createObjectURL(file);
      setEditImagePreview(previewUrl);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedQR) return;
    setIsSaving(true);
    try {
      await ownerUpdateQR(selectedQR.uuid, editFormData);
      // Refresh the list
      await getMyQrsAction();
      handleCloseModal();
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleLost = async () => {
    if (!selectedQR) return;
    try {
      await ownerToggleLost(selectedQR.uuid);
      // Refresh the list and update selectedQR
      await getMyQrsAction();
      // Update selectedQR's marked_as_lost status
      setSelectedQR((prev) => ({
        ...prev,
        marked_as_lost: !prev.marked_as_lost,
      }));
    } catch (error) {
      console.error("Toggle lost error:", error);
    }
  };

  const uploadPetPhoto = {
    color: "#595959",
    strokeWidth: 1.1,
    size: 25,
  };

  const iconStyle = {
    color: "#595959",
    strokeWidth: 1.5,
    size: 20,
  };

  const renderQRTable = () => {
    if (myQRS !== null && myQRS.length > 0) {
      return (
        <>
          <div className="pet-details-on-map">
            <div
              key={mapLocation?.name || "default"}
              className={`content-wrapper ${!mapLocation?.hasLocation && mapLocation ? "large-padding" : ""}`}
              style={{
                animation: "fadeIn 0.3s ease",
              }}
            >
              {!mapLocation ? (
                /* 1. საწყისი */
                <span>{t("select pet to see location")}</span>
              ) : !mapLocation.hasLocation ? (
                /* 2. ვერ მოიძებნა */
                <>
                  <FontAwesomeIcon icon={faLocationDot} />
                  <span>
                    {t("location not found", { petName: mapLocation.name })}
                  </span>
                </>
              ) : (
                /* 3. ნაპოვნია */
                <span>
                  {mapLocation.name} {t("last_seen")}:{" "}
                  {new Date(mapLocation.time).toLocaleString("ka-GE")}
                </span>
              )}
            </div>
          </div>

          <div className="qr-table-container">
            {/* My pet list long version */}
            <table className="qr-table">
              <thead>
                <tr>
                  <th>{t("pet_photo")}</th>
                  <th>{t("pet_name")}</th>
                  <th>{t("status")}</th>
                  <th>{t("city")}</th>
                  <th>{t("actions")}</th>
                </tr>
                <div className="pet-list-title-for-mobile">
                  <p className="my-pet-list-short-version-title">
                    {t("my pet list with qr tag")}
                  </p>
                  <Link className="add-tag" to="/order/qrcollar">
                    + <span>{t("add pet")}</span>
                  </Link>
                </div>
              </thead>
              <tbody>
                {myQRS.map((qr) => (
                  <tr
                    key={qr.id || qr.uuid}
                    onClick={() => handleQRClick(qr)}
                    className="qr-table-row"
                  >
                    <td className="qr-table-image-cell">
                      <img
                        src={qr.pet_image || "/img/pet-placeholder.webp"}
                        alt={qr.pet_name || t("unnamed_pet")}
                        className="qr-table-pet-image"
                      />
                    </td>
                    <td className="qr-table-name-cell">
                      {qr.pet_name || t("unnamed_pet")}
                    </td>
                    <td className="qr-table-actions-cell btn-for-mobile">
                      <button
                        onClick={(e) => handleShowOnMap(e, qr)}
                        style={{
                          animation: getPinAnimation(qr),
                          WebkitAnimation: getPinAnimation(qr),
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faLocationDot}
                          style={{ color: getPinColor(qr) }}
                        />
                      </button>
                      <a
                        onClick={(e) => {
                          // თუ ლოკაცია არ არის, გავაჩეროთ მოქმედება
                          if (!qr.last_location_lat || !qr.last_location_lng) {
                            e.preventDefault();
                            e.stopPropagation();
                            return;
                          }
                          e.stopPropagation(); // რომ ცხრილის row-ს onClick არ გააქტიურდეს
                        }}
                        // Google Maps-ის ლინკი: დანიშნულების ადგილი არის ცხოველის კოორდინატები
                        href={
                          qr.last_location_lat && qr.last_location_lng
                            ? `https://www.google.com/maps/dir/?api=1&destination=${qr.last_location_lat},${qr.last_location_lng}`
                            : "#"
                        }
                        target="_blank"
                        rel="noreferrer"
                        className={`route-btn ${!qr.last_location_lat || !qr.last_location_lng ? "disabled" : ""}`}
                        title={
                          qr.last_location_lat
                            ? t("get_directions")
                            : t("no_location_available")
                        }
                      >
                        <FontAwesomeIcon
                          icon={faLocationArrow}
                          style={{
                            opacity:
                              qr.last_location_lat && qr.last_location_lng
                                ? 1
                                : 0.4,
                            cursor:
                              qr.last_location_lat && qr.last_location_lng
                                ? "pointer"
                                : "not-allowed",
                          }}
                        />
                      </a>
                      <button>
                        <FontAwesomeIcon icon={faPencil} />
                      </button>
                    </td>

                    <td className="qr-table-status-2-cell">
                      <span
                        className={`status-badge ${qr.marked_as_lost ? "status-lost" : "status-safe"}`}
                      >
                        {qr.marked_as_lost
                          ? t("tag active")
                          : t("tag inactive")}
                      </span>
                      {/* <span>|</span>
                      <span>ID - {qr.qr_number}</span> */}
                    </td>
                    <td className="qr-table-status-cell">
                      <span
                        className={`status-badge ${qr.marked_as_lost ? "status-lost" : "status-safe"}`}
                      >
                        {qr.marked_as_lost
                          ? t("tag active")
                          : t("tag inactive")}
                      </span>
                    </td>
                    <td className="qr-table-city-cell">{qr.city || "-"}</td>
                    <td className="qr-table-actions-cell">
                      <button
                        className="qr-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQRClick(qr);
                        }}
                      >
                        {t("view_edit")}
                      </button>
                      {/* Show/Hide button hidden from user panel for now.
                          Backend endpoint and admin-side toggle remain active.
                          Re-enable when user-facing flow is ready.
                      <button
                        className="qr-detach-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleHiddenClick(qr);
                        }}
                        title={t(qr.hidden_by_owner ? "show_collar" : "hide_collar")}
                      >
                        {t(qr.hidden_by_owner ? "show_collar" : "hide_collar")}
                      </button>
                      */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* --- რუკის სექცია პირდაპირ გვერდზე --- */}
          <div id="pet-map-section" className="pet-map-parent-container">
            <div
              className="leaflet-container"
              // style={{
              //   height: "calc(100dvh - 370px)",
              //   width: "100%",
              //   overflow: "hidden",
              //   boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              //   border: "1px solid #eee",
              // }}
            >
              <MapContainer
                center={[41.7151, 44.8271]} // საწყისი წერტილი (თბილისი)
                zoom={11}
                scrollWheelZoom={true}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />

                {mapLocation && mapLocation.lat && mapLocation.lng && (
                  <>
                    <ChangeView center={[mapLocation.lat, mapLocation.lng]} />
                    <Marker
                      position={[mapLocation.lat, mapLocation.lng]}
                      icon={createAnimalIcon(
                        myQRS.find((q) => q.pet_name === mapLocation.name)
                          ?.pet_image,
                      )}
                    >
                      <Popup>
                        <strong>{mapLocation.name}</strong>
                        <br />
                        {t("last_seen")}:{" "}
                        {mapLocation.time
                          ? new Date(mapLocation.time).toLocaleString("ka-GE")
                          : "-"}
                      </Popup>
                    </Marker>
                  </>
                )}
              </MapContainer>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <div className="pet-details-on-map">
          <div
            className={`content-wrapper ${!mapLocation?.hasLocation && mapLocation ? "large-padding" : ""}`}
          >
            <span>{t("select pet to see location")}</span>
          </div>
        </div>

        <div
          className="qr-table-container"
          style={{
            background: "rgba(255, 255, 255, 0.35)",
            backdropFilter: "blur(10px)",
            webkitBackdropFilter: "blur(10px)",
          }}
        >
          <table className="qr-table">
            <thead>
              <div className="pet-list-title-for-mobile">
                <p className="my-pet-list-short-version-title">
                  {t("my pet list with qr tag")}
                </p>
                <Link className="add-tag" to="/order/qrcollar">
                  + <span>{t("add pet")}</span>
                </Link>
              </div>
            </thead>
          </table>
          <div className="empty-container" style={{ height: "100%" }}>
            <img src="/img/MyTagsEmpty.svg" alt="Empty Page" />
            <p>{t("my tags empty page title")}</p>
            <p>{t("my tags empty page description")}</p>
          </div>
        </div>

        <div id="pet-map-section" className="pet-map-parent-container">
          <div className="leaflet-container">
            <MapContainer
              center={[41.7151, 44.8271]} // საწყისი წერტილი (თბილისი)
              zoom={11}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
            </MapContainer>
          </div>
        </div>
      </>
    );
  };

  const renderEditModal = () => {
    if (!isModalOpen || !selectedQR) return null;

    return (
      <div className="qr-edit-modal-overlay" onClick={handleCloseModal}>
        <div className="qr-edit-modal" onClick={(e) => e.stopPropagation()}>
          <div className="qr-edit-modal-header">
            <button className="modal-close-btn" onClick={handleCloseModal}>
              <FontAwesomeIcon
                icon={faArrowLeft}
                size="sm"
                // className={
                //   darkMode ? "white-for-back-arrow" : "black-for-back-arrow"
                // }
              />
            </button>
            <h2>{t("edit_pet_info")}</h2>
          </div>

          <div className="qr-edit-modal-body">
            {/* Pet Image Upload Section */}
            <div className="form-group image-upload-group">
              <div className="image-upload-container">
                {editImagePreview ? (
                  <div className="image-preview-wrapper">
                    <img
                      src={editImagePreview}
                      alt="Pet preview"
                      className="pet-image-preview"
                    />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => {
                        setEditImagePreview(null);
                        setEditFormData((prev) => ({
                          ...prev,
                          pet_image: null,
                        }));
                      }}
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleEditImageChange}
                      className="image-upload-input"
                    />
                    <div className="image-upload-placeholder">
                      <ImagePlus {...uploadPetPhoto} />
                      <span>{t("add_pet_photo")}</span>
                    </div>
                  </label>
                )}
              </div>
            </div>
            {/* Pet Status Section */}
            <div className="form-section-title">{t("current_status")}</div>
            <div className="pet-status-section">
              <button
                className={`status-toggle-btn switch ${
                  selectedQR.marked_as_lost ? "btn-mark-safe" : "btn-mark-lost"
                }`}
                onClick={handleToggleLost}
                role="switch"
                aria-checked={selectedQR.marked_as_lost}
              >
                <span className="switch-label">{t("profile_open_lock")}</span>
                <span className="switch-track">
                  <span className="switch-thumb" />
                </span>
              </button>

              <div
                className={`pet-status-card ${
                  selectedQR.marked_as_lost ? "status-lost" : "status-safe"
                }`}
              >
                <div className="status-content">
                  <span className="status-description">
                    {selectedQR.marked_as_lost
                      ? t("lost_status_description")
                      : t("safe_status_description")}
                  </span>
                </div>
              </div>
            </div>
            {/* Pet Information Section */}
            <div className="form-section-title" style={{ paddingTop: "20px" }}>
              {t("pet_info")}
            </div>
            <div className="form-group">
              <Dog {...iconStyle} />
              <input
                type="text"
                name="pet_name"
                value={editFormData.pet_name || ""}
                onChange={handleEditFormChange}
                placeholder={t("enter_pet_name")}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <Dog {...iconStyle} />
                <select
                  name="pet_type"
                  value={editFormData.pet_type || "dog"}
                  onChange={handleEditFormChange}
                >
                  <option value="dog">{t("dog")}</option>
                  <option value="cat">{t("cat")}</option>
                  <option value="bird">{t("bird")}</option>
                </select>
              </div>

              <div className="form-group">
                <PawPrint {...iconStyle} />
                <input
                  type="text"
                  name="pet_breed"
                  value={editFormData.pet_breed || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_breed")}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <VenusAndMars {...iconStyle} />
                <select
                  name="gender"
                  value={editFormData.gender || "male"}
                  onChange={handleEditFormChange}
                >
                  <option value="male">{t("male")}</option>
                  <option value="female">{t("female")}</option>
                </select>
              </div>

              <div className="form-group">
                <Palette {...iconStyle} />
                <input
                  type="text"
                  name="pet_color"
                  value={editFormData.pet_color || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_color")}
                />
              </div>
            </div>
            <div className="form-group checkbox-group">
              <Syringe {...iconStyle} />
              <label>
                <input
                  type="checkbox"
                  name="is_vaccinated"
                  checked={editFormData.is_vaccinated || false}
                  onChange={handleEditFormChange}
                />
                <span>{t("vaccinated")}</span>
              </label>
            </div>
            {/* Owner Information Section */}
            <div className="form-section-title" style={{ paddingTop: "20px" }}>
              {t("owner_info")}
            </div>
            <div className="form-group">
              <User {...iconStyle} />
              <input
                type="text"
                name="full_name"
                value={editFormData.full_name || ""}
                onChange={handleEditFormChange}
                placeholder={t("enter_name")}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <Phone {...iconStyle} />
                <input
                  type="tel"
                  name="phone_number"
                  value={editFormData.phone_number || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_phone")}
                />
              </div>

              <div className="form-group">
                <Phone {...iconStyle} />
                <input
                  type="tel"
                  name="second_phone_number"
                  value={editFormData.second_phone_number || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_second_phone")}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <Building2 {...iconStyle} />
                <input
                  type="text"
                  name="city"
                  value={editFormData.city || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_city")}
                />
              </div>

              <div className="form-group">
                <MapPin {...iconStyle} />
                <input
                  type="text"
                  name="address"
                  value={editFormData.address || ""}
                  onChange={handleEditFormChange}
                  placeholder={t("enter_address")}
                />
              </div>
            </div>
            <div className="form-group">
              <MessageSquareMore {...iconStyle} />
              <textarea
                name="comment"
                value={editFormData.comment || ""}
                onChange={handleEditFormChange}
                placeholder={t("enter_comment")}
                rows="3"
              />
            </div>
            <button
              className="modal-save-btn"
              onClick={handleSaveEdit}
              disabled={isSaving}
            >
              {isSaving ? t("loading") : t("save")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="userpanel-container">
      <UserPanel />
      <div className="userpanel-screen-main-container">
        <div className="generate-qr-container">
          <div className="purchased-collars">
            {/* <div className="purchased-collars-header">
              <p>{t("purchased collars")}</p>
            </div> */}

            <div className="purchased-collars-table-wrapper">
              {renderQRTable()}
            </div>
          </div>
        </div>
      </div>

      {renderEditModal()}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    myQRS: state.orders.myQRS,
  };
};
export default connect(mapStateToProps, {
  getMyQrsAction,
  ownerUpdateQR,
  ownerToggleLost,
  toggleHiddenCollar,
})(GenerateQR);
